import { BeneficiaryType, Currency } from '@seapoint/server/enums';
import { Avatar } from '../../../../design-system/avatar/avatar/avatar';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';

type AccountDetailsConfig =
  | {
      beneficiaryType: BeneficiaryType.IBAN;
      iban: string;
    }
  | {
      beneficiaryType: BeneficiaryType.SCAN;
      accountNumber: string;
      sortCode: string;
    };

type PayrollPaymentRecordCardProps = {
  name: string;
  currency: Currency;
  amount: number;
  reference: string;
  accountDetails: AccountDetailsConfig;
};

export const PayrollPaymentRecordCard = ({
  name,
  currency,
  amount,
  reference,
  accountDetails,
}: PayrollPaymentRecordCardProps) => {
  const accountDetailsText = getAccountDetailsText({ accountDetails });
  const formattedAmount = getAmountWithCurrency({ amount, currency, isAccountBalance: true });

  return (
    <Inline space={3} className="w-full rounded-lg border border-seapoint-gray-border bg-white px-5 py-4">
      <Avatar name={name} size="sm" />

      <Stack space={1} className="min-w-0 flex-1">
        <h3 className="text w-full truncate text-nowrap text-start font-normal text-black">{name}</h3>

        <p className="w-full truncate text-start text-xs font-normal text-seapoint-gray-text">
          {reference} • {accountDetailsText}
        </p>
      </Stack>

      <div className="text font-medium text-black">{formattedAmount}</div>
    </Inline>
  );
};

type GetAccountDetailsTextArgs = {
  accountDetails: AccountDetailsConfig;
};

const getAccountDetailsText = ({ accountDetails }: GetAccountDetailsTextArgs) => {
  const { beneficiaryType } = accountDetails;

  switch (beneficiaryType) {
    case BeneficiaryType.IBAN:
      return accountDetails.iban;
    case BeneficiaryType.SCAN:
      return `${accountDetails.accountNumber} • ${accountDetails.sortCode}`;
    default:
      return exhaustiveCheck(beneficiaryType);
  }
};
